import axios from 'axios';
import { normalize } from 'normalizr';
import { toCollectionPersistenceDto } from '../../mappers/collections/toCollectionPersistenceDto';
import { toCollectionsModel } from '../../mappers/collections/toCollectionsModel';
import { collectionsSchema } from './schema';
import type {
    CollectionDto,
    CollectionModel,
    CollectionPersistenceModel,
    CollectionsDataModel,
} from './types';
import type { PostModel } from '../posts/types';

export const DEFAULT_COLLECTION_ID = '-1';

export async function loadCollections(): Promise<{
    collections: Array<CollectionsDataModel['id']>;
    records: {
        collections: CollectionsDataModel[];
    };
}> {
    const response = await axios.get('/collections');
    const collections = response.data.collections || [];
    const normalized = normalize(
        {
            collections: collections.map(toCollectionsModel),
        },
        {
            collections: [collectionsSchema],
        },
    );

    return {
        collections: normalized.result.collections,
        records: {
            collections: Object.values<CollectionsDataModel>(normalized.entities.collections ?? {}),
        },
    };
}

export async function loadCollection(
    collectionId: CollectionsDataModel['id'],
): Promise<CollectionModel> {
    const response = await axios.get(`/collections/${collectionId}`);

    return toCollectionsModel(response.data.collections[0]);
}

export async function saveToNewCollection(
    collectionName: string,
    postId: PostModel['id'],
): Promise<{
    collections: Array<CollectionsDataModel['id']>;
    records: {
        collections: CollectionsDataModel[];
    };
}> {
    const response = await axios('/collections', {
        method: 'POST',
        data: {
            post_id: postId,
            display_name: collectionName,
        },
    });
    const newCollections = response.data.collections.filter(
        (collection: CollectionDto) => collection.id !== DEFAULT_COLLECTION_ID,
    );

    const normalized = normalize(
        {
            collections: newCollections.map(toCollectionsModel),
        },
        {
            collections: [collectionsSchema],
        },
    );

    return {
        collections: normalized.result.collections,
        records: {
            collections: Object.values<CollectionsDataModel>(normalized.entities.collections ?? {}),
        },
    };
}

export async function saveToExistentCollection(
    collectionId: string,
    postId: PostModel['id'],
): Promise<{
    collections: Array<CollectionsDataModel['id']>;
    records: {
        collections: CollectionsDataModel[];
    };
}> {
    const response = await axios('/favourites', {
        method: 'POST',
        data: {
            post_id: postId,
            collection_id: collectionId,
        },
    });

    const collections = response.data.collections || [];
    const normalized = normalize(
        {
            collections: collections.map(toCollectionsModel),
        },
        {
            collections: [collectionsSchema],
        },
    );

    return {
        collections: normalized.result.collections,
        records: {
            collections: Object.values<CollectionsDataModel>(normalized.entities.collections ?? {}),
        },
    };
}

export async function removeCollection(collectionId: CollectionsDataModel['id']): Promise<void> {
    await axios.delete(`/collections/${collectionId}`);
}

export async function updateCollection(
    model: CollectionPersistenceModel,
): Promise<CollectionModel> {
    const response = await axios.put(`/collections/${model.id}`, toCollectionPersistenceDto(model));

    return toCollectionsModel(response.data.collections[0]);
}
